import { t } from "@lingui/macro";

import { i18n } from "base/i18n";
import { FILE_PREVIEW_EXTENSIONS } from "base/constants";

import {
  RE_TRY_LIMIT,
  THUMBNAIL_SUPPORTED_EXTENSIONS,
  ImageExtension
} from "./picker.constants";
import { FileProcessor } from "./file.processor";
import { UploadService } from "./upload.service";
import { Progress } from "./picker.progress";
import { usePickerDetailsStore } from "../libs/stores/filepicker";
import { sentryCaptureException } from "../components/primitives/errorboundary";

export class FileUpload {
  constructor(file, prefix, config) {
    this.file = file;
    this.id = file.id;
    this.prefix = prefix;
    this.file.progress = new Progress(this.file.setStatus);
    this.config = config;
    this.onFinishCallBack = this.onFinishCallBack.bind(this);
  }

  async multipartUpload(data, callback) {
    const { file } = this;
    const urls = data.Urls;
    this.file.key = data.Key;
    let uploads = [];
    let uploaded = 0;
    let processor = new FileProcessor(this.file, data.ChunkSize);
    const put = async (index, chunk) => {
      let partNumber = index + 1;
      uploads.push(
        safePut(urls[partNumber], chunk)
          .then((val) => {
            uploaded = uploaded + (chunk.byteLength / file.size) * 96.0;
            this.file.progress.setProgress(Math.round(uploaded));
            callback(null, {
              name: this.file.name,
              id: this.id,
              uploaded: uploaded
            });
            return val;
          })
          .catch((err) => {
            callback(err, null);
            this.file.progress.stopProgress();
          })
      );
    };
    await processor.run(put);
    const uploadPromises = await Promise.all(uploads);
    if (!uploadPromises.includes(undefined)) {
      UploadService.completeUpload({
        Key: data.Key,
        UploadId: data.UploadId
      })
        .then((response) => {
          this.uploadFinish(callback);
        })
        .catch((err) => {
          sentryCaptureException(err, "Multipart upload failed");
          callback(err, null);
          this.file.progress.stopProgress();
        });
    }
  }

  async singlePartUpload(data, callback) {
    const { file } = this;
    let blob = file.slice(0, file.size);
    this.file.progress.asyncProgress(5);
    let headers = { "Content-Type": "application/octet-stream" };
    // While get back file from s3 content type is important
    if (data.ContentType) {
      headers = { "Content-Type": data.ContentType };
    }
    safePut(data.Url, blob, headers)
      .then(() => {
        this.file.key = data.Key;
        this.file.progress.asyncProgress(70, 95);
        this.uploadFinish(callback);
      })
      .catch((err) => {
        callback(err, null);
        this.file.progress.stopProgress();
      });
  }

  async upload(url, callback = () => {}) {
    let data = {
      name: this.file.name,
      size: this.file.size,
      key: this.config.isProfile
        ? `${this.prefix}/${this.file.name}`
        : `${this.prefix}/${this.id}/${this.file.name}`,
      isProfile: this.config.isProfile
    };
    if (data.size === 0) {
      sentryCaptureException(new Error("Zero Filesize Caught", data));
    }

    let pickerDetails = usePickerDetailsStore.getState().getPickerDetails;
    pickerDetails().then(async (pickerData) => {
      this.filePickerDetails = pickerData;
      this.file.isAsync = !this.config.isSignature
        ? this.file.size > this.filePickerDetails.config.MinChunkSize
        : false;
      if (this.file.isAsync) {
        let response = await UploadService.initUpload(url, data);
        this.file.progress.asyncProgress(0, 20);
        this.multipartUpload(response, callback);
      } else {
        this.file.progress.asyncProgress(0, 5);
        let response = await UploadService.getSignedUrl(data, url);
        this.singlePartUpload(response, callback);
      }
    });
  }

  onFinishCallBack(callback, data) {
    if (this.config.Storage && this.config.Storage._id) {
      UploadService.uploadToDrive(this.config.Storage._id, data)
        .then((response) => {
          data.key = response.key;
          this.file.progress.setProgress(100);
          callback(null, data);
        })
        .catch((err) => {
          callback(err, null);
          this.file.progress.stopProgress();
        });
    } else {
      this.file.progress.setProgress(100);
      callback(null, data);
    }
  }

  uploadFinish(callback) {
    let fileObj = {
      uploaded: 100,
      name: this.file.name,
      id: this.file.id,
      key: this.file.key,
      ...(this.file.size && { size: this.file.size }),
      fileExtension: this.file.fileExtension
    };
    if (
      FILE_PREVIEW_EXTENSIONS.AUDIO_FORMATS.includes(this.file.fileExtension)
    ) {
      fileObj.duration = this.file.duration;
    }
    if (
      (THUMBNAIL_SUPPORTED_EXTENSIONS.includes(this.file.fileExtension) ||
        (this.file.fileExtension === ImageExtension.SVG &&
          this.config.isProfile)) &&
      !this.config.isSignature
    ) {
      generateThumbnail(
        this.file.key,
        this.config.imageProps.sizes,
        this.config.isProfile
      )
        .then(async (response) => {
          fileObj.photos = response;
          this.onFinishCallBack(callback, fileObj);
        })
        .catch((err) => {
          callback(err, null);
          this.file.progress.stopProgress();
        });
    } else {
      this.onFinishCallBack(callback, fileObj);
    }
  }
}

function wait(ms) {
  return new Promise((res) => setTimeout(res, ms));
}

async function safePut(
  url,
  data,
  headers = { "Content-Type": "application/octet-stream" },
  reTry = 0,
  err = { message: i18n._(t`Unexpected error occurred please contact support`) }
) {
  try {
    if (reTry < RE_TRY_LIMIT) {
      //NOTE: Exponential BackOff Retry
      reTry && (await wait(2 ** reTry * 1000));
      return await UploadService.uploadPart(url, {
        data: data,
        headers: headers
      }).catch((err) => {
        return Promise.reject(err);
      });
    }
    /** callback expect a object with error message  **/
    return Promise.reject(err);
  } catch (err) {
    return await safePut(url, data, headers, reTry + 1, err);
  }
}

export async function generateThumbnail(key, sizes, isProfile) {
  const [responsePromise] = UploadService.generateThumbnail({
    key: key,
    sizes: sizes,
    isProfile: !!isProfile
  });
  return await responsePromise();
}
